/**
 * WxbGanttChart v2 — Centralized State Management
 * Single useReducer + ref mirror for RAF rendering
 */
import { useReducer, useRef, Dispatch } from 'react';
import type { ViewMode, FlatRow } from './types';
import { DEFAULT_DAY_WIDTH, MIN_DAY_WIDTH, MAX_DAY_WIDTH } from './constants';

// ===== State =====
export interface GanttState {
  scrollX: number;
  scrollY: number;
  maxScrollX: number;        // scroll limit computed from content width
  maxScrollY: number;        // scroll limit computed from content height
  dayWidth: number;
  viewMode: ViewMode;
  collapsedGroups: Set<string>;
  expandedDay: number | null;
  /** Saved dayWidth before expanding a day, for restore on collapse */
  prevDayWidth: number | null;
  /** Saved scrollX before expanding a day, for restore on collapse */
  prevScrollX: number | null;
  hoveredTaskId: string | null;
  hoveredRow: number;         // -1 = none
  hoveredColX: number;        // canvas X of mouse, -1 = none
  selectedTaskIds: Set<string>;
  lastClickedTaskId: string | null;
  canvasW: number;
  canvasH: number;
  dirty: boolean;
}

// ===== Actions =====
export type GanttAction =
  | { type: 'SCROLL'; dx: number; dy: number }
  | { type: 'SET_SCROLL'; x?: number; y?: number }
  | { type: 'ZOOM'; dayWidth: number; anchorX?: number }
  | { type: 'SET_VIEW'; mode: ViewMode }
  | { type: 'TOGGLE_GROUP'; groupId: string }
  | { type: 'EXPAND_ALL' }
  | { type: 'COLLAPSE_ALL'; groupIds: string[] }
  | { type: 'EXPAND_DAY'; day: number | null }
  | { type: 'HOVER'; taskId: string | null }
  | { type: 'HOVER_ROW'; row: number; colX: number }
  | { type: 'SELECT'; taskId: string | null }
  | { type: 'SELECT_MULTI'; taskId: string }
  | { type: 'SELECT_RANGE'; taskId: string; flatRows: FlatRow[] }
  | { type: 'SELECT_ALL'; taskIds: string[] }
  | { type: 'SELECT_CLEAR' }
  | { type: 'SELECT_REMOVE'; taskId: string }
  | { type: 'RESIZE'; w: number; h: number }
  | { type: 'SET_MAX_SCROLL_Y'; maxY: number }
  | { type: 'SET_MAX_SCROLL_X'; maxX: number }
  | { type: 'MARK_DIRTY' }
  | { type: 'MARK_CLEAN' };

function createInitialState(dayWidth?: number): GanttState {
  return {
    scrollX: 0,
    scrollY: 0,
    maxScrollX: 0,
    maxScrollY: 0,
    dayWidth: dayWidth ?? DEFAULT_DAY_WIDTH,
    viewMode: 'day',
    collapsedGroups: new Set<string>(),
    expandedDay: null,
    prevDayWidth: null,
    prevScrollX: null,
    hoveredTaskId: null,
    hoveredRow: -1,
    hoveredColX: -1,
    selectedTaskIds: new Set<string>(),
    lastClickedTaskId: null,
    canvasW: 800,
    canvasH: 400,
    dirty: true,
  };
}

function clampScroll(v: number, max: number): number {
  return Math.max(0, Math.min(v, max));
}

function ganttReducer(state: GanttState, action: GanttAction): GanttState {
  switch (action.type) {
    case 'SCROLL': {
      const newX = clampScroll(state.scrollX + action.dx, state.maxScrollX);
      const newY = clampScroll(state.scrollY + action.dy, state.maxScrollY);
      if (newX === state.scrollX && newY === state.scrollY) return state;
      return { ...state, scrollX: newX, scrollY: newY, dirty: true };
    }
    case 'SET_SCROLL': {
      const newX = action.x !== undefined ? clampScroll(action.x, state.maxScrollX) : state.scrollX;
      const newY = action.y !== undefined ? clampScroll(action.y, state.maxScrollY) : state.scrollY;
      if (newX === state.scrollX && newY === state.scrollY) return state;
      return { ...state, scrollX: newX, scrollY: newY, dirty: true };
    }
    case 'ZOOM': {
      // Block zoom in expanded day mode (dayWidth is locked to canvasW)
      if (state.expandedDay !== null) return state;
      const clamped = Math.max(MIN_DAY_WIDTH, Math.min(MAX_DAY_WIDTH, action.dayWidth));
      if (clamped === state.dayWidth) return state;
      const ratio = clamped / state.dayWidth;
      let newScrollX: number;
      if (action.anchorX !== undefined) {
        // 锚点缩放: 保持鼠标所在世界坐标在缩放后仍映射到同一屏幕位置
        // worldX = scrollX + anchorX  (anchorX 是 canvas 局部 x，与 scrollX 同坐标系)
        const worldX = state.scrollX + action.anchorX;
        newScrollX = clampScroll(worldX * ratio - action.anchorX, state.maxScrollX);
      } else {
        // 无锚点(工具栏 +/- 按钮等): 等比保持可见区域中心
        newScrollX = clampScroll(state.scrollX * ratio, state.maxScrollX);
      }
      return { ...state, dayWidth: clamped, scrollX: newScrollX, dirty: true };
    }
    case 'SET_VIEW': {
      const presets: Record<ViewMode, number> = { hour: 600, day: 120, week: 60, month: 40 };
      return { ...state, viewMode: action.mode, dayWidth: presets[action.mode] ?? 120, scrollX: 0, scrollY: 0, dirty: true };
    }
    case 'TOGGLE_GROUP': {
      const next = new Set(state.collapsedGroups);
      if (next.has(action.groupId)) next.delete(action.groupId);
      else next.add(action.groupId);
      const newScrollY = clampScroll(state.scrollY, state.maxScrollY);
      return { ...state, collapsedGroups: next, scrollY: newScrollY, dirty: true };
    }
    case 'EXPAND_ALL': {
      return { ...state, collapsedGroups: new Set(), scrollY: 0, dirty: true };
    }
    case 'COLLAPSE_ALL': {
      return { ...state, collapsedGroups: new Set(action.groupIds), scrollY: 0, dirty: true };
    }
    case 'EXPAND_DAY': {
      if (action.day === null) {
        // Collapse: restore pre-expand zoom/scroll
        return {
          ...state,
          expandedDay: null,
          dayWidth: state.prevDayWidth ?? state.dayWidth,
          scrollX: state.prevScrollX ?? state.scrollX,
          prevDayWidth: null,
          prevScrollX: null,
          dirty: true,
        };
      }
      // Expand: set dayWidth = canvasW so 1 day fills entire viewport.
      // scrollX = 0, the render loop will override startHour/endHour.
      const prevDW = state.prevDayWidth ?? state.dayWidth;
      const prevSX = state.prevScrollX ?? state.scrollX;
      return {
        ...state,
        expandedDay: action.day,
        dayWidth: state.canvasW,  // 1 day = full viewport width
        scrollX: 0,               // no horizontal scroll in single-day mode
        maxScrollX: 0,             // lock horizontal scroll
        prevDayWidth: prevDW,
        prevScrollX: prevSX,
        dirty: true,
      };
    }
    case 'HOVER':
      if (action.taskId === state.hoveredTaskId) return state;
      return { ...state, hoveredTaskId: action.taskId, dirty: true };
    case 'HOVER_ROW': {
      if (action.row === state.hoveredRow && action.colX === state.hoveredColX) return state;
      return { ...state, hoveredRow: action.row, hoveredColX: action.colX, dirty: true };
    }
    case 'SELECT': {
      const next = new Set<string>();
      if (action.taskId) next.add(action.taskId);
      return { ...state, selectedTaskIds: next, lastClickedTaskId: action.taskId, dirty: true };
    }
    case 'SELECT_MULTI': {
      const next = new Set(state.selectedTaskIds);
      if (next.has(action.taskId)) next.delete(action.taskId);
      else next.add(action.taskId);
      return { ...state, selectedTaskIds: next, lastClickedTaskId: action.taskId, dirty: true };
    }
    case 'SELECT_RANGE': {
      if (!state.lastClickedTaskId) {
        const next = new Set<string>([action.taskId]);
        return { ...state, selectedTaskIds: next, lastClickedTaskId: action.taskId, dirty: true };
      }
      // Find range of task rows between lastClicked and current
      const rows = action.flatRows;
      let startIdx = -1, endIdx = -1;
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].taskId === state.lastClickedTaskId) startIdx = i;
        if (rows[i].taskId === action.taskId) endIdx = i;
      }
      if (startIdx === -1 || endIdx === -1) {
        return { ...state, selectedTaskIds: new Set([action.taskId]), lastClickedTaskId: action.taskId, dirty: true };
      }
      const lo = Math.min(startIdx, endIdx);
      const hi = Math.max(startIdx, endIdx);
      const next = new Set(state.selectedTaskIds);
      for (let i = lo; i <= hi; i++) {
        if (rows[i].type === 'task' && rows[i].taskId) next.add(rows[i].taskId!);
      }
      return { ...state, selectedTaskIds: next, dirty: true };
    }
    case 'SELECT_ALL':
      return { ...state, selectedTaskIds: new Set(action.taskIds), dirty: true };
    case 'SELECT_CLEAR':
      if (state.selectedTaskIds.size === 0) return state;
      return { ...state, selectedTaskIds: new Set(), lastClickedTaskId: null, dirty: true };
    case 'SELECT_REMOVE': {
      if (!state.selectedTaskIds.has(action.taskId)) return state;
      const next = new Set(state.selectedTaskIds);
      next.delete(action.taskId);
      return { ...state, selectedTaskIds: next, dirty: true };
    }
    case 'RESIZE': {
      // When resized while in expanded day mode, update dayWidth to match new canvasW
      if (state.expandedDay !== null) {
        return { ...state, canvasW: action.w, canvasH: action.h, dayWidth: action.w, maxScrollX: 0, dirty: true };
      }
      return { ...state, canvasW: action.w, canvasH: action.h, dirty: true };
    }
    case 'SET_MAX_SCROLL_Y':
      if (action.maxY === state.maxScrollY) return state;
      return { ...state, maxScrollY: action.maxY };
    case 'SET_MAX_SCROLL_X':
      // In expanded day mode, always keep maxScrollX = 0
      if (state.expandedDay !== null) return state;
      if (action.maxX === state.maxScrollX) return state;
      return { ...state, maxScrollX: action.maxX };
    case 'MARK_DIRTY':
      return state.dirty ? state : { ...state, dirty: true };
    case 'MARK_CLEAN':
      return state.dirty ? { ...state, dirty: false } : state;
    default:
      return state;
  }
}

export interface GanttStore {
  state: GanttState;
  dispatch: Dispatch<GanttAction>;
  stateRef: React.MutableRefObject<GanttState>;
}

export function useGanttStore(initialDayWidth?: number): GanttStore {
  const [state, dispatch] = useReducer(ganttReducer, initialDayWidth, createInitialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  return { state, dispatch, stateRef };
}
